import { FormikProps } from 'formik'
import { useEffect, forwardRef } from 'react'
import { Input, Select, Textarea } from '@dgshahr/ui-kit'
import IconStoreIcon from '@/components/common/icons/IconStore'
import L, { LeafletMouseEvent } from 'leaflet'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { MapComponent } from './MapComponent'
import { useNeshan } from '@/api/neshan/CreateNeshan'
import faToEnNum from '@/utils/functions/convertNumbers'
import { IFormValues } from './FormAddress'
import { useGetCities } from '@/api/cities/GetCities'

interface IProvince {
  id: number
  title: string
}

interface ICity {
  id: number
  title: string
  province: number
}

interface IWarehouseAddressFormProps {
  formik: FormikProps<IFormValues>
  provinces: IProvince[]
}

const WarehouseAddressForm = forwardRef<
  HTMLDivElement,
  IWarehouseAddressFormProps
>(({ formik, provinces }, ref) => {
  const { mutate: getCities, data: cities } = useGetCities()
  const filteredCities = cities?.data

  const { mutate: neshan } = useNeshan({ disableToast: true })

  const customIcon = new L.Icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  })

  useEffect(() => {
    if (formik.values.warehouseProvinces) {
      getCities(formik.values.warehouseProvinces)
    }
  }, [formik.values.warehouseProvinces, getCities])

  const handleMapClick = (e: LeafletMouseEvent) => {
    formik.setFieldValue('warehouseLat', e.latlng.lat)
    formik.setFieldValue('warehouseLng', e.latlng.lng)
  }

  const handleWarehouseBlur = async (
    e: React.FocusEvent<HTMLTextAreaElement>
  ) => {
    const address = formik.values.warehouseAddress
    neshan(
      { address },
      {
        onSuccess: (responseData) => {
          if (responseData?.latitude && responseData?.longitude) {
            formik.setFieldValue('warehouseLat', responseData.latitude)
            formik.setFieldValue('warehouseLng', responseData.longitude)
          }
        }
      }
    )
  }

  return (
    <div ref={ref}>
      <p className="flex mb-5">
        <IconStoreIcon className="size-6 text-primary-500" />
        <span className="font-p1-medium pr-2">آدرس انبار:</span>
      </p>
      <div className="tablet:grid tablet:grid-cols-2 tablet:gap-4">
        <Select
          inputProps={{
            labelContent: 'استان',
            placeholder: 'استان را انتخاب کنید',
            name: 'warehouseProvinces',
            errorMessage:
              formik.touched.warehouseProvinces &&
              formik.errors.warehouseProvinces
                ? formik.errors.warehouseProvinces
                : undefined,
            required: true
          }}
          wrapperClassName="mb-5"
          onChange={(value) => {
            formik.setFieldValue('warehouseProvinces', value)
            formik.setFieldValue('warehouseCities', null)
          }}
          value={formik.values.warehouseProvinces}
          mode="single"
          options={provinces.map((province) => ({
            label: province.title,
            value: province.id
          }))}
        />
        <Select
          inputProps={{
            labelContent: 'شهر',
            placeholder: 'شهر را انتخاب کنید',
            name: 'warehouseCities',
            errorMessage:
              formik.touched.warehouseCities && formik.errors.warehouseCities
                ? formik.errors.warehouseCities
                : undefined,
            required: true
          }}
          wrapperClassName="mb-5"
          onChange={(value) => formik.setFieldValue('warehouseCities', value)}
          value={formik.values.warehouseCities}
          mode="single"
          options={
            filteredCities?.map((city: ICity) => ({
              label: city.title,
              value: city.id
            })) ?? []
          }
        />
        <Textarea
          required
          name="warehouseAddress"
          id="warehouseAddress"
          containerClassName="bg-warning-500"
          wrapperClassName="col-start-1 col-end-3 mb-5"
          labelContent="آدرس"
          value={formik.values.warehouseAddress}
          onChange={formik.handleChange}
          onBlur={handleWarehouseBlur}
          errorMessage={
            formik.touched.warehouseAddress && formik.errors.warehouseAddress
              ? formik.errors.warehouseAddress
              : undefined
          }
        />
        <Input
          required
          name="warehouseDetail"
          id="warehouseDetail"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.warehouseDetail}
          labelContent="جزئیات"
          placeholder="مثال: پلاک 1، واحد 1"
          type="text"
          errorMessage={
            formik.touched.warehouseDetail && formik.errors.warehouseDetail
              ? formik.errors.warehouseDetail
              : undefined
          }
        />
        <Input
          required
          name="warehousePostalCode"
          id="warehousePostalCode"
          onChange={(e) => {
            const convertedValue = faToEnNum(e.target.value)
            formik.setFieldValue('warehousePostalCode', convertedValue)
          }}
          wrapperClassName="my-5 tablet:my-0"
          value={formik.values.warehousePostalCode}
          labelContent="کدپستی"
          placeholder="کدپستی 10 رقمی را وارد کنید"
          type="tel"
          errorMessage={
            formik.touched.warehousePostalCode &&
            formik.errors.warehousePostalCode
              ? formik.errors.warehousePostalCode
              : undefined
          }
        />
        <div className="col-start-1 col-end-3 pb-5 border-b border-gray-100 mx-auto w-full tablet:mt-4">
          <MapComponent
            center={[formik.values.warehouseLat, formik.values.warehouseLng]}
            zoom={17}
            markerPosition={[
              formik.values.warehouseLat,
              formik.values.warehouseLng
            ]}
            onMapClick={handleMapClick}
            className="w-full tablet:w-3/4 tablet:ms-auto h-[442px] tablet:h-[684px]"
            icon={customIcon}
          />
        </div>
      </div>
    </div>
  )
})

WarehouseAddressForm.displayName = 'WarehouseAddressForm'

export default WarehouseAddressForm
